import type { StrategyFormState, StrategyAction, DraftRate, WageRate } from '../../types/strategy';
import { getRaceUnitName, getUnitPointsLabel } from '../../constants/game';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { NumericInput } from './NumericInput';

interface MilitaryTabProps {
  state: StrategyFormState;
  dispatch: React.Dispatch<StrategyAction>;
  totalLand: number;
  population: number;
  race?: string;
  /** SF-172: `'touch'` renders 44px-min-height/16px-font `NumericInput`s (mobile); default
   *  preserves today's exact desktop styling. */
  inputSize?: 'default' | 'touch';
  /** SF-172: forwarded to every `NumericInput`'s own `resetKey` — clears any in-progress typed
   *  draft on an external bulk mutation (e.g. loading a template mid-edit). */
  resetKey?: number;
}

const DRAFT_RATES: { value: DraftRate; label: string; cost: string }[] = [
  { value: 'NONE', label: 'None', cost: '0 gc' },
  { value: 'RESERVIST', label: 'Reservist', cost: '30 gc' },
  { value: 'NORMAL', label: 'Normal', cost: '50 gc' },
  { value: 'AGGRESSIVE', label: 'Aggressive', cost: '75 gc' },
  { value: 'EMERGENCY', label: 'Emergency', cost: '110 gc' },
];

const WAGE_RATES: { value: WageRate; label: string }[] = [
  { value: 50, label: '50%' },
  { value: 100, label: '100%' },
  { value: 150, label: '150%' },
  { value: 200, label: '200%' },
];

const selectStyle = {
  ...INPUT_STYLE,
  border: `1px solid ${COLORS.inputBorder}`,
} as const;

export function MilitaryTab({ state, dispatch, totalLand, population, race, inputSize, resetKey }: MilitaryTabProps) {
  const wizards = Math.floor(state.wizardPA * totalLand);
  const nonWizardPop = population - wizards;
  const army = Math.floor(nonWizardPop * (state.draftLevel / 100));
  const soldiers = Math.floor(army * 0.35);
  const trainable = army - soldiers;

  const unitRows = [
    { key: 'offSpec' as const, label: getRaceUnitName(race, 'offense'), points: getUnitPointsLabel(race, 'offense'), count: Math.floor(state.armyComp.offSpec * totalLand) },
    { key: 'defSpec' as const, label: getRaceUnitName(race, 'defense'), points: getUnitPointsLabel(race, 'defense'), count: Math.floor(state.armyComp.defSpec * totalLand) },
    { key: 'elite' as const, label: getRaceUnitName(race, 'elite'), points: getUnitPointsLabel(race, 'elite'), count: Math.floor(state.armyComp.elite * totalLand) },
  ];
  const allocated = unitRows.reduce((s, u) => s + u.count, 0);
  const thieves = Math.floor(state.thiefPA * totalLand);

  return (
    <div className="space-y-6">
      {/* Draft Level */}
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span style={{ color: COLORS.text.secondary }}>Draft Level</span>
          <span className="font-mono" style={{ color: COLORS.text.primary }}>{state.draftLevel}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={80}
          step={1}
          value={state.draftLevel}
          onChange={(e) => dispatch({ type: 'SET_DRAFT_LEVEL', value: parseInt(e.target.value) })}
          className="w-full accent-blue-500"
          aria-label="Draft Level"
        />
      </div>

      {/* Draft Rate & Wage Rate — side by side */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-sm mb-1" style={{ color: COLORS.text.secondary }}>Draft Rate</div>
          <select
            value={state.draftRate}
            onChange={(e) => dispatch({ type: 'SET_DRAFT_RATE', value: e.target.value as DraftRate })}
            className="w-full rounded text-sm py-1.5 px-2"
            style={selectStyle}
          >
            {DRAFT_RATES.map((r) => (
              <option key={r.value} value={r.value}>{r.label} ({r.cost})</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-sm mb-1" style={{ color: COLORS.text.secondary }}>Wage Rate</div>
          <select
            value={state.wageRate}
            onChange={(e) => dispatch({ type: 'SET_WAGE_RATE', value: parseInt(e.target.value) as WageRate })}
            className="w-full rounded text-sm py-1.5 px-2"
            style={selectStyle}
          >
            {WAGE_RATES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Army Composition */}
      <div>
        <div className="text-sm mb-2" style={{ color: COLORS.text.secondary }}>Army Composition (per acre)</div>

        {/* Allocation bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1" style={{ color: COLORS.text.tertiary }}>
            <span>Army Allocation</span>
            <span className={allocated > trainable ? 'text-yellow-400' : ''} style={allocated > trainable ? undefined : { color: COLORS.text.secondary }}>
              {allocated.toLocaleString()} / {trainable.toLocaleString()}
            </span>
          </div>
          <div className="w-full rounded-full h-2 overflow-hidden" style={{ backgroundColor: COLORS.border }}>
            <div
              className={`h-full rounded-full transition-all ${allocated > trainable ? 'bg-yellow-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min(trainable > 0 ? (allocated / trainable) * 100 : 0, 100)}%` }}
            />
          </div>
          {allocated > trainable && (
            <div className="text-xs text-yellow-400 mt-1">
              Over-allocated by {(allocated - trainable).toLocaleString()} — not enough trainable soldiers.
            </div>
          )}
        </div>

        <div className="grid grid-cols-[1fr_70px_70px] gap-2 text-xs px-1 mb-1" style={{ color: COLORS.text.tertiary }}>
          <span>Unit</span>
          <span className="text-right">Per Acre</span>
          <span className="text-right">Count</span>
        </div>
        {unitRows.map(({ key, label, points, count }) => (
          <div key={key} className="grid grid-cols-[1fr_70px_70px] gap-2 items-center px-1 py-1">
            <span className="text-sm" style={{ color: COLORS.text.secondary }}>
              {label} <span className="text-xs" style={{ color: COLORS.text.tertiary }}>{points}</span>
            </span>
            <NumericInput
              value={state.armyComp[key]}
              step={0.5}
              onChange={(v) => dispatch({ type: 'SET_ARMY_COMP', unit: key, value: v })}
              className="w-16 ml-auto"
              ariaLabel={`${label} per acre`}
              size={inputSize}
              resetKey={resetKey}
            />
            <div className="text-right text-sm font-mono" style={{ color: COLORS.text.secondary }}>
              {count.toLocaleString()}
            </div>
          </div>
        ))}

        {/* Thieves row */}
        <div className="grid grid-cols-[1fr_70px_70px] gap-2 items-center px-1 py-1 mt-1" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <span className="text-sm" style={{ color: COLORS.text.secondary }}>Thieves</span>
          <NumericInput
            value={state.thiefPA}
            step={0.5}
            onChange={(v) => dispatch({ type: 'SET_THIEF_PA', value: v })}
            className="w-16 ml-auto"
            ariaLabel="Thieves per acre"
            size={inputSize}
            resetKey={resetKey}
          />
          <div className="text-right text-sm font-mono" style={{ color: COLORS.text.secondary }}>
            {thieves.toLocaleString()}
          </div>
        </div>

        {/* Wizards row */}
        <div className="grid grid-cols-[1fr_70px_70px] gap-2 items-center px-1 py-1">
          <span className="text-sm" style={{ color: COLORS.text.secondary }}>
            Wizards <span className="text-xs" style={{ color: COLORS.text.tertiary }}>(outside draft)</span>
          </span>
          <NumericInput
            value={state.wizardPA}
            step={0.5}
            onChange={(v) => dispatch({ type: 'SET_WIZARD_PA', value: v })}
            className="w-16 ml-auto"
            ariaLabel="Wizards per acre"
            size={inputSize}
            resetKey={resetKey}
          />
          <div className="text-right text-sm font-mono" style={{ color: COLORS.text.secondary }}>
            {wizards.toLocaleString()}
          </div>
        </div>

      </div>
    </div>
  );
}
